"use client"

import { useQuery } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import { getQueryTenantId } from "@/lib/auth/query-tenant-id"
import type { CrmPipeline, CrmStage } from "@/lib/crm/types"

export type ActiveCrmIntegrationView = {
  id: string
  name: string
  isActive: boolean
}

export const activeCrmIntegrationsQueryKey = (tenantId: string) =>
  ["crm-integrations", tenantId, "active"] as const

export const crmPipelinesQueryKey = (
  tenantId: string,
  integrationId: string,
) => ["crm-integrations", tenantId, integrationId, "pipelines"] as const

export function useActiveCrmIntegrationsQuery() {
  const { user, isAuthenticated, fetchWithAuth } = useAuth()
  const tenantId = getQueryTenantId(user)

  return useQuery<ActiveCrmIntegrationView[]>({
    queryKey: activeCrmIntegrationsQueryKey(tenantId),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const response = await fetchWithAuth("/api/crm/integrations?active_only=true")
      const body = await readJsonObject(response)
      if (!response.ok) {
        throw new Error(readApiError(body, "CRM integrations could not be loaded."))
      }
      return readActiveCrmIntegrations(body)
    },
  })
}

export function useCrmPipelinesQuery(integrationId: string) {
  const { user, isAuthenticated, fetchWithAuth } = useAuth()
  const tenantId = getQueryTenantId(user)

  return useQuery<CrmPipeline[]>({
    queryKey: crmPipelinesQueryKey(tenantId, integrationId),
    enabled: isAuthenticated && integrationId.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const response = await fetchWithAuth(
        `/api/crm/integrations/${encodeURIComponent(integrationId)}/pipelines`,
      )
      const body = await readJsonObject(response)
      if (!response.ok) {
        throw new Error(readApiError(body, "CRM pipelines could not be loaded."))
      }
      return readCrmPipelines(body)
    },
  })
}

async function readJsonObject(response: Response): Promise<Record<string, unknown>> {
  const body: unknown = await response.json().catch(() => ({}))
  if (!body || typeof body !== "object" || Array.isArray(body)) return {}
  return body as Record<string, unknown>
}

function readApiError(
  body: Record<string, unknown>,
  fallback: string,
): string {
  return typeof body.error === "string" ? body.error : fallback
}

function readActiveCrmIntegrations(
  body: Record<string, unknown>,
): ActiveCrmIntegrationView[] {
  if (!Array.isArray(body.integrations)) return []
  return body.integrations.flatMap((value): ActiveCrmIntegrationView[] => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return []
    const integration = value as Record<string, unknown>
    if (
      typeof integration.id !== "string"
      || typeof integration.name !== "string"
      || integration.isActive !== true
    ) {
      return []
    }
    return [{
      id: integration.id,
      name: integration.name,
      isActive: true,
    }]
  })
}

function readCrmPipelines(body: Record<string, unknown>): CrmPipeline[] {
  if (!Array.isArray(body.pipelines)) return []
  return body.pipelines.flatMap((value): CrmPipeline[] => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return []
    const pipeline = value as Record<string, unknown>
    if (
      typeof pipeline.id !== "string"
      || typeof pipeline.name !== "string"
      || !Array.isArray(pipeline.stages)
    ) {
      return []
    }
    return [{
      id: pipeline.id,
      name: pipeline.name,
      ...(typeof pipeline.isDefault === "boolean"
        ? { isDefault: pipeline.isDefault }
        : {}),
      stages: readCrmStages(pipeline.stages),
    }]
  })
}

function readCrmStages(values: unknown[]): CrmStage[] {
  return values.flatMap((value): CrmStage[] => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return []
    const stage = value as Record<string, unknown>
    if (
      typeof stage.id !== "string"
      || typeof stage.name !== "string"
      || typeof stage.pipelineId !== "string"
      || typeof stage.order !== "number"
    ) {
      return []
    }
    return [{
      id: stage.id,
      name: stage.name,
      pipelineId: stage.pipelineId,
      order: stage.order,
      ...(typeof stage.color === "string" ? { color: stage.color } : {}),
      ...(typeof stage.isArchived === "boolean"
        ? { isArchived: stage.isArchived }
        : {}),
    }]
  })
}
